import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";

// ============================================================================
// Types
// ============================================================================

export interface ProjectOut {
  id: string;
  nombre: string;
  descripcion: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  num_documents?: number | null;
  my_role?: "owner" | "member" | null;
}

export interface ProjectMemberOut {
  user_id: string;
  email: string;
  nombre: string;
  role: "owner" | "member";
  created_at?: string;
}

export interface UserSearchOut {
  id: string;
  email: string;
  nombre: string;
}

// Tolera respuesta como array plano o envuelta en {items}/{sessions}/etc.
function asList<T>(data: unknown, key?: string): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    for (const k of [key, "items", "results"].filter(Boolean) as string[]) {
      if (Array.isArray(obj[k])) return obj[k] as T[];
    }
  }
  return [];
}

// ============================================================================
// Query Keys
// ============================================================================

export const projectKeys = {
  all: ["projects"] as const,
  list: () => [...projectKeys.all, "list"] as const,
  detail: (id: string) => [...projectKeys.all, "detail", id] as const,
  members: (id: string) => [...projectKeys.all, "members", id] as const,
};

// ============================================================================
// Hooks
// ============================================================================

export function useProjects() {
  return useQuery<ProjectOut[], Error>({
    queryKey: projectKeys.list(),
    queryFn: async () =>
      asList<ProjectOut>(await fetchWithAuth("/api/v1/projects"), "projects"),
  });
}

export function useProject(projectId: string) {
  return useQuery<ProjectOut, Error>({
    queryKey: projectKeys.detail(projectId),
    queryFn: () => fetchWithAuth(`/api/v1/projects/${projectId}`),
    enabled: !!projectId,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation<ProjectOut, Error, { nombre: string; descripcion?: string }>({
    mutationFn: (body) =>
      fetchWithAuth("/api/v1/projects", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.list() });
      showToast.success("Proyecto creado");
    },
    onError: (e) => showToast.error(e.message || "Error al crear el proyecto"),
  });
}

export function useUpdateProject(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<ProjectOut, Error, { nombre?: string; descripcion?: string }>({
    mutationFn: (body) =>
      fetchWithAuth(`/api/v1/projects/${projectId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      showToast.success("Proyecto actualizado");
    },
    onError: (e) =>
      showToast.error(e.message || "Error al actualizar el proyecto"),
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (projectId) =>
      fetchWithAuth(`/api/v1/projects/${projectId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.list() });
      showToast.success("Proyecto eliminado");
    },
    onError: (e) =>
      showToast.error(e.message || "Error al eliminar el proyecto"),
  });
}

export function useProjectMembers(projectId: string) {
  return useQuery<ProjectMemberOut[], Error>({
    queryKey: projectKeys.members(projectId),
    queryFn: async () =>
      asList<ProjectMemberOut>(
        await fetchWithAuth(`/api/v1/projects/${projectId}/members`),
        "members"
      ),
    enabled: !!projectId,
  });
}

export function useAddProjectMember(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    ProjectMemberOut,
    Error,
    { email: string; role: "owner" | "member" }
  >({
    mutationFn: (body) =>
      fetchWithAuth(`/api/v1/projects/${projectId}/members`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.members(projectId) });
      showToast.success("Miembro añadido");
    },
    onError: (e) => showToast.error(e.message || "Error al añadir el miembro"),
  });
}

export function useRemoveProjectMember(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (userId) =>
      fetchWithAuth(`/api/v1/projects/${projectId}/members/${userId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.members(projectId) });
      showToast.success("Miembro eliminado");
    },
    onError: (e) =>
      showToast.error(e.message || "Error al eliminar el miembro"),
  });
}

export function useSearchUsers(q: string) {
  return useQuery<UserSearchOut[], Error>({
    queryKey: ["users", "search", q],
    queryFn: async () =>
      asList<UserSearchOut>(
        await fetchWithAuth(`/api/v1/users/search?q=${encodeURIComponent(q)}`),
        "users"
      ),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
  });
}
